<template>
  <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid }">
    <div class="card mb-2">
      <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
        <b-form class="search-form" @submit.prevent="handleSubmit(onSearch)">
          <div class="d-flex">
            <div class="column">
              <div class="row align-items-center">
                <div class="input-wrapper">
                  <b-form-group class="mb-0">
                    <date-range-picker
                        style="width: 100%"
                        @update="onSaleDateRangeApply"
                        showWeekNumbers
                        showDropdowns
                        ref="picker"
                        opens="right"
                        :locale-data="{ firstDay: 1, format: 'DD-MM-YYYY HH:mm:ss' }"
                        v-model="saleDateRange"
                        :config="{ clearButton: true }">
                      <template v-slot:input="picker" style="min-width: 350px;">
                        <span v-if="!picker.startDate" class="text-muted">
                          {{ $t('Sale Date Range') }}
                        </span>
                        <span v-else>
                          {{ picker.startDate | toDate }} - {{ picker.endDate | toDate }}
                        </span>
                      </template>
                    </date-range-picker>
                  </b-form-group>
                </div>

                <div class="input-wrapper">
                  <department-select-input-widget :model="searchModel" :label="false" @unselect="clearCheckboxes"
                      :options="{placeholder: $t('Sale Department')}" multiple attribute="department_uuids"/>
                </div>

                <div class="input-wrapper" style="width: 450px;">
                  <input-widget :model="searchModel" attribute="sale_no" type="tags" :label="false"
                      :placeholder="$t('Sale No')" @change="clearCheckboxes"/>
                </div>
              </div>

              <div class="row align-items-center">
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_registered_on_sale" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_not_registered_on_sale"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_not_registered_on_sale" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_registered_on_sale"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_bidder" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_not_bidder"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_not_bidder" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_bidder"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_underbidder" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_not_underbidder"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_not_underbidder" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_underbidder"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_winning_bidder" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_not_winning_bidder"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_not_winning_bidder" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_winning_bidder"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_buyer" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_not_buyer || searchModel.is_seller "/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_not_buyer" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_buyer"/>
                </div>
              </div>
              <div class="row align-items-center">
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_vendor" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_not_vendor || searchModel.is_seller"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_not_vendor" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_vendor"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_seller" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_not_seller || searchModel.is_vendor|| searchModel.is_buyer"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_not_seller" type="checkbox"
                      :disabled="disableSellerBuyerFilters || searchModel.is_seller"/>
                </div>
              </div>
              <div class="row align-items-center">
                <div class="input-wrapper">
                  <input-widget :placeholder="$t('Total Hammer (Buyer) Min')" attribute="total_hammer_buyer_min"
                      :model="searchModel" type="number" :label="false" @input="checkInput('total_hammer_buyer_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :placeholder="$t('Total Hammer (Buyer) Max')" attribute="total_hammer_buyer_max"
                      :label="false" type="number" :model="searchModel" @input="checkInput('total_hammer_buyer_max')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_invoice_min" :label="false" type="number"
                      :placeholder="$t('Total Invoice (Buyer) Min')" @input="checkInput('total_invoice_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_invoice_max" :label="false" type="number"
                      :placeholder="$t('Total Invoice (Buyer) Max')" @input="checkInput('total_invoice_max')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_invoice_paid_min" :label="false" type="number"
                      :placeholder="$t('Total Invoice Paid (Buyer) Min')" @input="checkInput('total_invoice_paid_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_invoice_paid_max" :label="false" type="number"
                      :placeholder="$t('Total Invoice Paid (Buyer) Max')" @input="checkInput('total_invoice_paid_max')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="single_hammer_buyer_min" :label="false" type="number"
                      :placeholder="$t('Single Hammer (Buyer) Min')" @input="checkInput('single_hammer_buyer_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="single_hammer_buyer_max" :label="false" type="number"
                      :placeholder="$t('Single Hammer (Buyer) Max')" @input="checkInput('single_hammer_buyer_max')"/>
                </div>
              </div>

              <div class="row align-items-center">
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_hammer_seller_min" :label="false" type="number"
                      :placeholder="$t('Total Hammer (Seller) Min')" @input="checkInput('total_hammer_seller_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_hammer_seller_max" :label="false" type="number"
                      :placeholder="$t('Total Hammer (Seller) Max')" @input="checkInput('total_hammer_seller_max')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_settlement_min" :label="false" type="number"
                      :placeholder="$t('Total Settlement (Seller) Min')" @input="checkInput('total_settlement_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_settlement_max" :label="false" type="number"
                      :placeholder="$t('Total Settlement (Seller) Max')" @input="checkInput('total_settlement_max')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_settlement_paid_min" :label="false" type="number"
                      :placeholder="$t('Total Settl. Paid (Seller) Min')" @input="checkInput('total_settlement_paid_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="total_settlement_paid_max" :label="false" type="number"
                      :placeholder="$t('Total Settl. Paid (Seller) Max')" @input="checkInput('total_settlement_paid_max')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="single_hammer_seller_min" :label="false" type="number"
                      :placeholder="$t('Single Hammer (Seller) Min')" @input="checkInput('single_hammer_seller_min')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="single_hammer_seller_max" :label="false" type="number"
                      :placeholder="$t('Single Hammer (Seller) Max')" @input="checkInput('single_hammer_seller_max')"/>
                </div>
              </div>
              <div class="row align-items-center">
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="billing_address" :label="false"
                      :placeholder="$t('Billing Address')"/>
                </div>
                <div class="input-wrapper">
                  <country-select-input-widget attribute="billing_country_codes" :model="searchModel" :label="false"
                      :options="{placeholder: $t('Billing Country')}" multiple/>
                </div>
                <div class="input-wrapper" style="width: 450px;">
                  <input-widget :model="searchModel" attribute="billing_states" type="tags" :label="false"
                      :placeholder="$t('Billing State')"/>
                </div>
                <div class="input-wrapper" style="width: 450px;">
                  <input-widget :model="searchModel" attribute="billing_cities" type="tags" :label="false"
                      :placeholder="$t('Billing City')"/>
                </div>
                <div class="input-wrapper d-flex align-items-start" style="width: 450px;">
                  <input-widget class="flex-grow-1 mb-0" :model="searchModel" attribute="billing_zips" type="tags"
                      :label="false" :placeholder="$t('Billing Zip/Postcode')" :tag-validator="zipTagValidator"
                      :invalid-tag-text="zipInvalidTagText"/>
                  <font-awesome-icon icon="info-circle" class="ml-2 mt-2 text-info hover-cursor"
                      v-b-tooltip.hover :title="zipTooltip"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="shipping_address" :label="false"
                      :placeholder="$t('Shipping Address')"/>
                </div>
                <div class="input-wrapper">
                  <country-select-input-widget attribute="shipping_country_codes" :model="searchModel" :label="false"
                      :options="{placeholder: $t('Shipping Country')}" multiple/>
                </div>
                <div class="input-wrapper" style="width: 450px;">
                  <input-widget :model="searchModel" attribute="shipping_states" type="tags" :label="false"
                      :placeholder="$t('Shipping States')"/>
                </div>
                <div class="input-wrapper" style="width: 450px;">
                  <input-widget :model="searchModel" attribute="shipping_cities" type="tags" :label="false"
                      :placeholder="$t('Shipping City')"/>
                </div>
                <div class="input-wrapper d-flex align-items-start" style="width: 450px;">
                  <input-widget class="flex-grow-1 mb-0" :model="searchModel" attribute="shipping_zips" type="tags"
                      :label="false" :placeholder="$t('Shipping Zip/Postcode')" :tag-validator="zipTagValidator"
                      :invalid-tag-text="zipInvalidTagText"/>
                  <font-awesome-icon icon="info-circle" class="ml-2 mt-2 text-info hover-cursor"
                      v-b-tooltip.hover :title="zipTooltip"/>
                </div>
              </div>
              <div class="row align-items-center">
                <div class="input-wrapper">
                  <input-widget :model="searchModel" type="select2" :items="saleSources" value-field="key" :label="false"
                      display-field="${item.value}" attribute="sale_sources" :options="{placeholder: $t('Bid Source')}"
                      multiple/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="is_company" type="checkbox"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="company_name" :label="false"
                      :placeholder="$t('Company Name')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :items="customerAcquisitionSources" value-field="value" display-field="${item.key}"
                          :label="false" type="select2" :model="searchModel" attribute="acquisition_sources"
                          :options="{placeholder: $t('Acquisition Source')}" multiple/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" type="select2" :items="existingTags" value-field="key" :label="false"
                      display-field="${item.text}" attribute="tags" :options="{placeholder: $t('Tags')}" multiple/>
                </div>
                <div class="input-wrapper">
                  <user-select-input-widget :model="searchModel" :label="false" attribute="account_managers_uuids"
                      :options="{placeholder: $t('Account Manager')}" multiple/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" type="select2" :items="customerStatuses" value-field="value" multiple
                      display-field="${item.key}" attribute="statuses" :options="{placeholder: $t('Status')}"
                      :label="false"/>
                </div>
                <div class="input-wrapper">
                  <input-widget type="select2" :options="{placeholder: $t('Trust Level')}" attribute="trust_levels_uuids"
                      :model="searchModel" display-field="${item.name}" value-field="value" :items="trustLevels"
                      :label="false" multiple/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="vat_number" :label="false"
                      :placeholder="$t('VAT Number')"/>
                </div>
                <div class="input-wrapper">
                  <input-widget :model="searchModel" attribute="customer_interests" :label="false"
                      :placeholder="$t('Customer Interests')"/>
                </div>
                <div class="input-wrapper">
                  <audience-select-input-widget @unselect="onAudienceUnselect" @select="onAudienceSelect" :model="searchModel"
                                               attribute="audience_uuids" multiple
                                               :label="false" :options="{placeholder: $t('Customer Audience'), multiple: true}"
                                               :items="searchModel.audiences"/>
                </div>
              </div>
            </div>
            <div class="search-btn-wrapper text-right">
              <b-button variant="primary" @click.prevent="onResetClick">
                <font-awesome-icon icon="undo" class="icon"/>
                {{ $t('Reset') }}
              </b-button>
              <b-button type="submit" variant="primary" class="ml-2">
                <font-awesome-icon icon="search" class="icon"/>
                {{ $t('Search') }}
              </b-button>
            </div>
          </div>
        </b-form>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "@/core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import DateRangePicker from 'vue2-daterange-picker'
  import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
  import UserSelectInputWidget from '@/core/widgets/UserSelectInputWidget';
  import DepartmentSelectInputWidget from '@/core/widgets/DepartmentSelectInputWidget';
  import CountrySelectInputWidget from '@/core/widgets/CountrySelectInputWidget';
  import ReportsService from "@/modules/reports/ReportsService";
  import AudienceSelectInputWidget from "@/core/widgets/AudienceSelectInputWidget";
  import {isValidZipPattern} from "@/modules/marketing/customers/zipPattern";

  const {mapState, mapActions} = createNamespacedHelpers('marketing/customer');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "CustomerSearch",
    components: {
      AudienceSelectInputWidget,
      UserSelectInputWidget,
      InputWidget,
      DateRangePicker,
      DepartmentSelectInputWidget,
      CountrySelectInputWidget
    },
    props: {
      trustLevels: {
        type: Array,
        default: () => []
      },
      audiences: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        saleDateRange: {startDate: null, endDate: null},
        existingTags: []
      }
    },
    computed: {
      ...mapState({
        searchModel: state => state.tableData.searchModel
      }),
      ...globalMapState({
        customerAcquisitionSources: state => state.customerAcquisitionSources,
        customerStatuses: state => state.customerStatuses,
        customerGenders: state => state.customerGenders,
        saleSources: state => state.saleSources
      }),
      disableSellerBuyerFilters() {
        return !this.searchModel.saleDateRange.startDate && !this.searchModel.department_uuids.length && !this.searchModel.sale_no.length;
      },
      zipTooltip() {
        return this.$t('Exact value matches exactly. Use * as a wildcard: 78* = starts with 78, *78 = ends with 78, *78* = contains 78.');
      },
      zipInvalidTagText() {
        return this.$t('Use * only at the start and/or end of the value, e.g. 78*, *78 or *78*');
      }
    },
    methods: {
      ...mapActions(['getTableData', 'resetTable', 'resetTableDataPaginationPage']),
      zipTagValidator(tag) {
        return isValidZipPattern(tag);
      },
      async onSearch() {
        this.resetTableDataPaginationPage();
        this.getTableData();
      },
      checkInput(inputName) {
        const otherInputName = inputName.endsWith('_min') ? inputName.replace('_min', '_max') : inputName.replace('_max', '_min')
        if (![null, undefined, ''].includes(this.searchModel[inputName])) {
          this.searchModel.rules = {...this.searchModel.rules, [otherInputName]: 'required'}
        } else {
          delete this.searchModel.rules[otherInputName];
          this.searchModel.rules = {...this.searchModel.rules};
        }
      },
      onResetClick() {
        this.saleDateRange = {startDate: null, endDate: null};
        this.resetTable();
      },
      onSaleDateRangeApply() {
        this.searchModel.saleDateRange = this.saleDateRange;
      },
      clearCheckboxes() {
        if (!this.searchModel.saleDateRange.endDate && !this.searchModel.department_uuids.length && !this.searchModel.sale_no.length) {
          this.searchModel.is_registered_on_sale = false;
          this.searchModel.is_bidder = false;
          this.searchModel.is_underbidder = false;
          this.searchModel.is_winning_bidder = false;
          this.searchModel.is_buyer = false;
          this.searchModel.is_vendor = false;
          this.searchModel.is_seller = false;
        }
      },
      async onAudienceSelect(audience) {
        this.searchModel.audiences.push(audience)
      },
      async onAudienceUnselect(audience) {
        this.searchModel.audiences = this.searchModel.audiences.filter(r => r.uuid !== audience.uuid)
      },
    },
    async mounted() {
      if (this.searchModel && this.searchModel.saleDateRange) {
        this.saleDateRange = this.searchModel.saleDateRange;
      }
      let tagsResponse = await ReportsService.getExistingTags();
      if (tagsResponse.success) {
        this.existingTags = tagsResponse.body.map(val => {
          return {
            key: val, text: val.split('_').map(x=>x.replace(/^./, x[0].toUpperCase())).join(' ')
          }
        });
      }
    }
  }
</script>

<style lang="scss" scoped>
</style>
